import { Chip, TextField } from '@mui/material';
import Autocomplete, { AutocompleteProps } from '@mui/material/Autocomplete';
import React, { useRef, useState } from 'react';

////////////////////////////////////////////////////////////////////////////

export interface IMprcChipInputExItem {
    name: string;
    canDelete: boolean;
    icon?: any;
}

interface IProps {
    id?: string;
    label?: string;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    error?: boolean;
    helperText?: React.ReactNode;
    selectedItems: IMprcChipInputExItem[];

    inputRef?: any;
    clickable?: boolean;
    onChange: (items: IMprcChipInputExItem[]) => void;
    onClick?: (item: IMprcChipInputExItem) => void;
}

type Props<T> = IProps &
    Omit<AutocompleteProps<T, boolean, boolean, boolean>,
        "value" | "options" | "clickable" | "renderInput" | "onClick" | "onChange" | "loading" | "isOptionEqualToValue" | "renderTags" | "helperText" | "multiple" | "disabled">;

////////////////////////////////////////////////////////////////////////////

/**
 * Componente para incluir array de string em chip
 * @param props
 */
export const MprcChipInputEx: React.FC<Props<string>> = (props) => {

    const { selectedItems, label, placeholder, clickable, error, helperText, inputRef, onClick, onChange,
        id = new Date().getTime().toString(),
        required = false,
        disabled = false,
    } = props;

    const [inputValue, setInputValue] = useState<string>("");
    const options = useRef<IMprcChipInputExItem[]>([]);

    ////////////////////////////////////////////////////////////////////////////
    /// HANDLES
    ////////////////////////////////////////////////////////////////////////////

    // Verifica se a tecla pressionada é o [Enter] e inclui o item
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.keyCode == 13 && inputValue.length > 0) {
            event.preventDefault();
            if (!selectedItems.map(x => x.name.toLocaleLowerCase()).includes(inputValue.toLocaleLowerCase())) {
                onChange([...selectedItems, { name: inputValue, canDelete: true }]);
                setInputValue("");
            }
        }
    };

    //
    const handleAddItem = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.keyCode == 13 && inputValue.length > 0) {
            event.preventDefault();
        }
    };

    // Exclui o item pelo icone excluir do Chip
    const handleDeleteChip = (item: IMprcChipInputExItem | null) => {
        if (selectedItems && item && item.canDelete) {
            const items = selectedItems.filter(x => x.name != item.name);
            onChange(items);
        }
    };

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <Autocomplete
            id={id}
            fullWidth
            disableClearable
            multiple
            disabled={disabled}
            openText=""
            open={false}
            clearOnEscape
            freeSolo
            options={options.current}
            inputValue={inputValue}
            value={selectedItems}
            isOptionEqualToValue={(option, value) => option === value}
            filterSelectedOptions={false}
            filterOptions={(options) => options}
            renderInput={(params) => (
                <TextField
                    error={error}
                    helperText={helperText}
                    {...params}
                    variant="standard"
                    label={label}
                    placeholder={placeholder}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        handleKeyDown(e);
                        handleAddItem(e);
                    }}
                    required={required}
                    fullWidth
                    slotProps={{
                        input: {
                            ...params.InputProps,
                        }
                    }}
                    inputRef={inputRef}
                />
            )}
            renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => {
                    if (option) {
                        return (
                            <Chip
                                icon={option.icon}
                                label={option.name}
                                clickable={clickable}
                                onClick={() => onClick && onClick(option)}
                                {...getTagProps({ index })}
                                size="small"
                                onDelete={option.canDelete ? () => handleDeleteChip(option) : undefined}
                            />);
                    }
                    return null;
                })
            }

        />
    );
};
